(function () {
  function parseJsonScript(root, selector) {
    const script = root.querySelector(selector);
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('Failed to parse quantity presets goodie config:', error);
      return null;
    }
  }

  function getVariantDisplayLabel(variant) {
    const title = (variant?.title || '').trim();
    if (!title || title === 'Default Title') return '';
    return title;
  }

  function getVariantImage(variant, gift) {
    if (variant?.image) return variant.image;
    return gift?.fallbackImage || null;
  }

  function generateProbeSetInstanceId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID().replace(/-/g, '');
    }
    return Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
  }

  function probeSetLineProperties(instanceId, properties) {
    const lineProps = { _ProbeSetInstance: instanceId };
    if (properties) {
      Object.assign(lineProps, properties);
    }
    return lineProps;
  }

  function probeSetGiftLineProperties(instanceId) {
    return {
      _ProbeSetGift: '1',
      _ProbeSetInstance: instanceId
    };
  }

  class QuantityPresetsGoodie {
    constructor(root) {
      this.root = root;
      this.config = parseJsonScript(root, '[data-quantity-presets-goodie-config]');
      if (!this.config?.gift) return;

      this.gift = this.config.gift;
      this.threshold = parseInt(this.config.threshold, 10) || 2;
      this.productFormId = this.config.productFormId;
      this.declineLabel = this.config.declineLabel || 'Nein, ich möchte keinen';
      this.acceptLabel = this.config.acceptLabel || 'Ja, ich möchte einen';
      this.drawerTitle = this.config.drawerTitle || 'Wähle deinen Shaker';
      this.selectedVariantId = null;
      this.declined = true;
      this.isSubmitting = false;

      this.panel = root.querySelector('[data-quantity-presets-goodie-panel]');
      this.slotButton = root.querySelector('[data-quantity-presets-goodie-slot]');
      this.slotLabel = root.querySelector('[data-colors-slot-label]');
      this.slotImage = root.querySelector('[data-colors-slot-image]');
      this.slotRemove = root.querySelector('[data-quantity-presets-goodie-remove]');
      this.slotCheck = root.querySelector('[data-slot-check]');

      this.drawer = root.querySelector('x-drawer');
      this.drawerList = root.querySelector('[data-quantity-presets-goodie-drawer-list]');
      this.drawerStatus = root.querySelector('[data-quantity-presets-goodie-drawer-status]');
      this.drawerConfirm = root.querySelector('[data-quantity-presets-goodie-drawer-confirm]');
      this.drawerConfirmCount = root.querySelector('[data-quantity-presets-goodie-drawer-confirm-count]');

      const blockItem = root.closest('.product-info__block-item') || root.closest('.product-info__quantity-selector');
      this.quantityInput = blockItem?.querySelector('.quantity-selector__input');
      this.productForm = document.getElementById(this.productFormId);

      if (!this.quantityInput || !this.productForm || !this.slotButton || !this.drawer) return;

      this._bindEvents();
      this._updateSlotUi();
      this._updateVisibility();
      this.root.hidden = false;
      this.root.setAttribute('data-initialized', 'true');
    }

    _bindEvents() {
      this.quantityInput.addEventListener('change', () => this._updateVisibility());
      this.quantityInput.addEventListener('input', () => this._updateVisibility());

      this.slotButton.addEventListener('click', (event) => {
        event.preventDefault();
        this._openDrawer();
      });

      this.slotRemove?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.declined = true;
        this.selectedVariantId = null;
        this._updateSlotUi();
      });

      this.drawerConfirm?.addEventListener('click', (event) => {
        event.preventDefault();
        this._closeDrawer();
      });

      this.productForm.addEventListener('submit', (event) => this._onFormSubmit(event), true);
    }

    _getCurrentQuantity() {
      return parseInt(this.quantityInput.value, 10) || 0;
    }

    _goodieApplies() {
      return this._getCurrentQuantity() >= this.threshold;
    }

    _updateVisibility() {
      const applies = this._goodieApplies();
      if (this.panel) {
        this.panel.hidden = !applies;
      }
      if (!applies) {
        this._closeDrawer();
        this.slotButton?.classList.remove('is-missing-highlight');
      }
    }

    _getSelectedVariant() {
      if (!this.selectedVariantId) return null;
      return this.gift.variants.find((variant) => variant.id === this.selectedVariantId) || null;
    }

    _updateSlotUi() {
      const variant = this._getSelectedVariant();
      const hasSelection = Boolean(variant);

      if (this.slotLabel) {
        if (hasSelection) {
          const label = getVariantDisplayLabel(variant);
          this.slotLabel.textContent = label || variant.title;
          this.slotLabel.hidden = false;
          this.slotLabel.classList.remove('text-subdued');
        } else {
          this.slotLabel.textContent = this.declineLabel;
          this.slotLabel.hidden = false;
          this.slotLabel.classList.add('text-subdued');
        }
      }

      if (this.slotImage) {
        const imageUrl = hasSelection
          ? getVariantImage(variant, this.gift)
          : this.gift.fallbackImage;
        if (imageUrl) {
          this.slotImage.innerHTML = `<img src="${imageUrl}" alt="" loading="lazy" width="60" height="60">`;
        }
      }

      if (this.slotButton) {
        this.slotButton.classList.toggle('is-filled', hasSelection);
        this.slotButton.classList.remove('is-missing-highlight');
      }

      if (this.slotCheck) {
        this.slotCheck.setAttribute('aria-hidden', hasSelection ? 'false' : 'true');
      }

      if (this.slotRemove) {
        this.slotRemove.hidden = !hasSelection;
      }
    }

    _bindRowEvents(row) {
      const input = row.querySelector('input[type="radio"]');
      const persist = () => {
        requestAnimationFrame(() => {
          this._persistDrawerSelection();
          this._syncDrawerState();
        });
      };

      input?.addEventListener('change', persist);
      row.addEventListener('click', persist);
    }

    _createDeclineRow(checked) {
      const row = document.createElement('label');
      row.className =
        'bk-media-bundle-builder__drawer-item bk-media-bundle-builder__colors-variant-row bk-media-bundle-builder__colors-variant-row--opt-in-decline';
      row.setAttribute('data-colors-variant-row', '');
      if (checked) row.classList.add('is-selected');

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'quantity-presets-goodie-choice';
      input.value = 'decline';
      input.checked = checked;

      const info = document.createElement('div');
      info.className = 'bk-media-bundle-builder__drawer-item-info v-stack gap-1';

      const title = document.createElement('span');
      title.className = 'text-base bk-media-bundle-builder__drawer-item-title';
      title.textContent = this.declineLabel;
      info.appendChild(title);

      row.appendChild(input);
      row.appendChild(info);
      this._bindRowEvents(row);
      return row;
    }

    _createAcceptRow(variant, checked, useAcceptLabel) {
      const row = document.createElement('label');
      row.className = 'bk-media-bundle-builder__drawer-item bk-media-bundle-builder__colors-variant-row';
      row.setAttribute('data-colors-variant-row', '');
      if (!variant.available) row.classList.add('is-unavailable');
      if (checked) row.classList.add('is-selected');

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'quantity-presets-goodie-choice';
      input.value = String(variant.id);
      input.checked = checked;
      input.disabled = !variant.available;

      const imageWrap = document.createElement('div');
      imageWrap.className = 'bk-media-bundle-builder__drawer-item-image';
      const imageUrl = getVariantImage(variant, this.gift);
      if (imageUrl) {
        imageWrap.innerHTML = `<img src="${imageUrl}" alt="" loading="lazy" width="56" height="56">`;
      }

      const info = document.createElement('div');
      info.className = 'bk-media-bundle-builder__drawer-item-info v-stack gap-1';

      const title = document.createElement('span');
      title.className = 'text-base bk-media-bundle-builder__drawer-item-title';
      title.textContent = useAcceptLabel
        ? this.acceptLabel
        : getVariantDisplayLabel(variant) || variant.title;
      info.appendChild(title);

      if (!variant.available) {
        const soldOut = document.createElement('span');
        soldOut.className = 'text-xs bk-media-bundle-builder__colors-variant-sold-out';
        soldOut.textContent = 'Ausverkauft';
        info.appendChild(soldOut);
      }

      row.appendChild(input);
      if (imageUrl) row.appendChild(imageWrap);
      row.appendChild(info);
      this._bindRowEvents(row);
      return row;
    }

    _renderDrawer() {
      if (!this.drawerList) return;
      this.drawerList.innerHTML = '';

      const declined = this.declined || !this.selectedVariantId;
      this.drawerList.appendChild(this._createDeclineRow(declined));

      const variants = this.gift.variants || [];
      if (variants.length === 1) {
        const variant = variants[0];
        const checked = Boolean(this.selectedVariantId) && !this.declined;
        this.drawerList.appendChild(this._createAcceptRow(variant, checked, true));
      } else {
        variants.forEach((variant) => {
          const checked =
            !this.declined && String(this.selectedVariantId) === String(variant.id);
          this.drawerList.appendChild(this._createAcceptRow(variant, checked, false));
        });
      }

      this._syncDrawerState();
    }

    _getDrawerCheckedInput() {
      return this.drawerList?.querySelector('input[name="quantity-presets-goodie-choice"]:checked');
    }

    _getDrawerSelectedVariant() {
      const checked = this._getDrawerCheckedInput();
      if (!checked || checked.value === 'decline') return null;
      return this.gift.variants.find((variant) => String(variant.id) === checked.value) || null;
    }

    _persistDrawerSelection() {
      const checked = this._getDrawerCheckedInput();
      if (!checked) return;

      if (checked.value === 'decline') {
        this.declined = true;
        this.selectedVariantId = null;
        this._updateSlotUi();
        return;
      }

      const variant = this._getDrawerSelectedVariant();
      if (!variant?.available) return;

      this.declined = false;
      this.selectedVariantId = variant.id;
      this._updateSlotUi();
    }

    _syncDrawerState() {
      const checked = this._getDrawerCheckedInput();
      const variant = this._getDrawerSelectedVariant();
      const declined = checked?.value === 'decline';
      const selected = declined || variant?.available ? 1 : 0;

      if (this.drawerConfirmCount) {
        this.drawerConfirmCount.textContent = `${selected}/1 ausgewählt`;
      }

      if (this.drawerStatus) {
        this.drawerStatus.textContent =
          variant && !variant.available ? 'Diese Variante ist ausverkauft.' : '';
      }

      if (this.drawerConfirm) {
        this.drawerConfirm.disabled = selected !== 1;
      }

      this.drawerList?.querySelectorAll('[data-colors-variant-row]').forEach((row) => {
        const input = row.querySelector('input[type="radio"]');
        row.classList.toggle('is-selected', Boolean(input?.checked));
      });
    }

    _openDrawer() {
      if (!this.drawer || !this.drawerList) return;
      this._renderDrawer();
      this.drawer.show?.();
    }

    _closeDrawer() {
      if (!this.drawer) return;
      this.drawer.hide?.();
      if (this.drawerStatus) this.drawerStatus.textContent = '';
    }

    _getLineItemProperties(formData) {
      const properties = {};
      formData.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && value !== '') {
          properties[match[1]] = value;
        }
      });
      return properties;
    }

    async _onFormSubmit(event) {
      if (this.isSubmitting || !this._goodieApplies() || this.declined || !this.selectedVariantId) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.isSubmitting) return;

      const formData = new FormData(this.productForm);
      const mainVariantId = parseInt(formData.get('id'), 10);
      const quantity = parseInt(formData.get('quantity'), 10) || 1;
      if (!mainVariantId) return;

      const properties = this._getLineItemProperties(formData);
      const instanceId = generateProbeSetInstanceId();
      const mainItem = {
        id: mainVariantId,
        quantity,
        properties: probeSetLineProperties(instanceId, properties)
      };

      const items = [
        mainItem,
        {
          id: parseInt(this.selectedVariantId, 10),
          quantity: 1,
          properties: probeSetGiftLineProperties(instanceId)
        }
      ];
      await this._addItemsToCart(items);
    }

    async _addItemsToCart(items) {
      const cartDrawerElement = document.querySelector('cart-drawer');
      const sectionId = cartDrawerElement?.getAttribute('data-section-id') || 'cart-drawer';
      let sectionsToBundle = ['variant-added'];

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        })
      );

      if (!sectionsToBundle.includes(sectionId)) {
        sectionsToBundle.push(sectionId);
      }

      const submitButtons = Array.from(this.productForm.elements).filter(
        (element) => element.type === 'submit'
      );

      try {
        this.isSubmitting = true;
        submitButtons.forEach((button) => {
          button.setAttribute('disabled', 'disabled');
          button.setAttribute('aria-busy', 'true');
        });

        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items,
            sections: sectionsToBundle.join(',')
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.description || 'Failed to add items to cart');
        }

        const responseJson = await response.json();
        const cartContent = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
        if (!cartContent.sections) cartContent.sections = {};
        Object.assign(cartContent.sections, responseJson.sections || {});
        if (window.BkMediaBundleCartPricing?.attachFreshDrawerSection) {
          await window.BkMediaBundleCartPricing.attachFreshDrawerSection(cartContent);
        }

        this.productForm.dispatchEvent(
          new CustomEvent('variant:add', {
            bubbles: true,
            cancelable: true,
            detail: {
              items: responseJson.items || [responseJson],
              cart: cartContent
            }
          })
        );

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              baseEvent: 'variant:add',
              cart: cartContent
            }
          })
        );
      } catch (error) {
        console.error('Error adding products to cart:', error);
        this.productForm.dispatchEvent(
          new CustomEvent('cart:error', {
            bubbles: true,
            detail: { error: error.message }
          })
        );
      } finally {
        this.isSubmitting = false;
        submitButtons.forEach((button) => {
          button.removeAttribute('disabled');
          button.removeAttribute('aria-busy');
        });
      }
    }
  }

  function initQuantityPresetsGoodie() {
    document.querySelectorAll('[data-quantity-presets-goodie]:not([data-initialized])').forEach((root) => {
      new QuantityPresetsGoodie(root);
    });
  }

  document.addEventListener('DOMContentLoaded', initQuantityPresetsGoodie);
  document.addEventListener('shopify:section:load', () => {
    setTimeout(initQuantityPresetsGoodie, 100);
  });
  document.addEventListener('product:rerender', () => {
    setTimeout(initQuantityPresetsGoodie, 100);
  });

  if (document.readyState !== 'loading') {
    setTimeout(initQuantityPresetsGoodie, 100);
  }
})();
